import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';

interface BlogPost {
  title: string;
  content: string;
}

const BlogPostCreator: React.FC = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Form inputs
  const [topic, setTopic] = useState(() => localStorage.getItem('bpc_topic') || '');
  const [description, setDescription] = useState(() => localStorage.getItem('bpc_description') || '');
  const [targetAudience, setTargetAudience] = useState(() => localStorage.getItem('bpc_audience') || 'General readers');
  const [tone, setTone] = useState(() => localStorage.getItem('bpc_tone') || 'Professional');
  const [length, setLength] = useState(() => localStorage.getItem('bpc_length') || 'Medium (800-1200 words)');
  
  // Editor states
  const [blogPost, setBlogPost] = useState<BlogPost | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [editMode, setEditMode] = useState(false);
  const contentEditorRef = useRef<HTMLDivElement>(null);
  
  // Save to localStorage on change
  React.useEffect(() => {
    localStorage.setItem('bpc_topic', topic);
    localStorage.setItem('bpc_description', description);
    localStorage.setItem('bpc_audience', targetAudience);
    localStorage.setItem('bpc_tone', tone);
    localStorage.setItem('bpc_length', length);
  }, [topic, description, targetAudience, tone, length]);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      // Don't navigate away, just show demo below
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate inputs
    if (!topic || !description) {
      toast.error('Please provide both a topic and description');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Creating your blog post...');

      // Get user ID for authentication
      const userId = await getToken();
      
      if (!userId) {
        toast.error('Authentication error. Please log in again.');
        navigate('/login');
        return;
      }

      // Call the backend endpoint with user ID in request body
      const response = await fetch(
        `${ENV.API_URL}/api/marketing/blog-post`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId,
            topic,
            description,
            targetAudience,
            tone,
            length
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate blog post');
      }

      const data = await response.json();
      toast.dismiss();
      setBlogPost(data.blogPost);
      setEditedContent(data.blogPost.content);
      toast.success('Blog post created successfully!');
    } catch (error) {
      toast.dismiss();
      console.error('Error creating blog post:', error);
      toast.error(error instanceof Error ? error.message : 'An error occurred while creating your blog post');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
    if (!editMode && contentEditorRef.current) {
      contentEditorRef.current.focus();
    }
  };

  const handleContentChange = (e: React.FormEvent<HTMLDivElement>) => {
    setEditedContent(e.currentTarget.innerHTML);
  };

  const copyToClipboard = () => {
    if (blogPost) {
      const content = editMode ? editedContent : blogPost.content;
      const fullContent = `${blogPost.title}\n\n${content.replace(/<[^>]*>/g, '')}`;
      navigator.clipboard.writeText(fullContent);
      toast.success('Blog post copied to clipboard as plain text!');
    }
  };
  
  const copyHtmlToClipboard = () => {
    if (blogPost) {
      const content = editMode ? editedContent : blogPost.content;
      const fullHtmlContent = `<h1>${blogPost.title}</h1>\n${content}`;
      navigator.clipboard.writeText(fullHtmlContent);
      toast.success('Blog post HTML copied to clipboard!');
    }
  };

  // Handle case where user is not logged in
  if (!user) {
    // Demo blog post example
    return (
      <div className="p-6 text-center relative overflow-hidden">
        {/* Decorative blurred orbs */}
        <div className="absolute top-40 -left-24 w-72 h-72 bg-gradient-to-br from-indigo-200 to-blue-300 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -top-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-200 to-indigo-300 rounded-full opacity-20 blur-3xl"></div>
        
        <div className="relative z-10 mb-8">
          <h2 className="text-3xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Try the Blog Post Creator Demo</h2>
          
          <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-6 max-w-2xl mx-auto mb-6 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 opacity-70"></div>
            
            <div className="relative z-10">
              <h3 className="text-xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-700">Digital Marketing Trends for 2025</h3>
              
              <div className="text-left text-sm mb-3 py-2 px-3 bg-white/30 backdrop-blur-sm rounded-lg border border-white/40">
                <span className="font-medium">Target Audience:</span> <span className="text-gray-700">Business professionals</span><br/>
                <span className="font-medium">Tone:</span> <span className="text-gray-700">Professional</span><br/>
                <span className="font-medium">Length:</span> <span className="text-gray-700">Medium (800-1200 words)</span>
              </div>
              
              <div className="text-left text-gray-700 mb-4 bg-white/20 backdrop-blur-sm rounded-lg p-3 border border-white/30">
                The digital marketing landscape is evolving at a rapid pace. In 2025, businesses must embrace AI-driven personalization, omnichannel strategies, and privacy-first data practices to stay competitive. This post explores the top trends shaping the future of digital marketing and how you can leverage them to grow your brand.
              </div>
              
              <div className="text-left text-gray-800 border-t border-indigo-100 pt-3 mt-3">
                <p className="font-medium mb-2">Excerpt:</p>
                <div className="pl-3 space-y-2">
                  <p className="flex">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs mr-2 flex-shrink-0">1</span>
                    <span><span className="font-semibold">AI-Powered Personalization:</span> Marketers will use AI to deliver hyper-personalized experiences at scale.</span>
                  </p>
                  <p className="flex">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs mr-2 flex-shrink-0">2</span>
                    <span><span className="font-semibold">Omnichannel Engagement:</span> Seamless integration across platforms will be essential.</span>
                  </p>
                  <p className="flex">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs mr-2 flex-shrink-0">3</span>
                    <span><span className="font-semibold">Data Privacy:</span> New regulations will require transparent, ethical data use.</span>
                  </p>
                  <p className="text-center text-gray-400 mt-2">...</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative z-10">
            <p className="text-lg text-gray-700 mb-2">Sign up or log in to create your own custom blog post!</p>
            <button className="mt-2 py-2 px-6 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105">
              Sign In to Get Started
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-10 max-w-6xl relative overflow-hidden">
      {/* Decorative blurred orbs */}
      <div className="absolute top-40 -left-24 w-72 h-72 bg-gradient-to-br from-indigo-200 to-blue-300 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute -top-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-200 to-indigo-300 rounded-full opacity-20 blur-3xl"></div>
      
      <div className="relative z-10 mb-8">
        <h1 className="text-4xl font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Blog Post Creator</h1>
        <p className="text-gray-700 text-lg max-w-3xl">
          Generate engaging blog posts from a brief description to boost your content marketing
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left column - Input form */}
        <div className="w-full lg:w-2/5 relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-6">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 opacity-70"></div>
          
          <div className="relative z-10">
            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-5">Blog Details</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2" htmlFor="topic">
                Blog Topic <span className="text-blue-500">*</span>
              </label>
              <input
                type="text"
                id="topic"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="E.g. Digital Marketing Trends for 2025 (try: 'How to grow a SaaS business')"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2" htmlFor="description">
                Brief Description <span className="text-blue-500">*</span>
              </label>
              <textarea
                id="description"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800 h-28"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what you want to cover in your blog post (e.g. 'Latest strategies for SaaS growth in 2025')"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2" htmlFor="targetAudience">
                Target Audience
              </label>
              <select
                id="targetAudience"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
              >
                <option value="General readers">General readers</option>
                <option value="Business professionals">Business professionals</option>
                <option value="Technical audience">Technical audience</option>
                <option value="Beginners">Beginners</option>
                <option value="Industry experts">Industry experts</option>
                <option value="Small business owners">Small business owners</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2" htmlFor="tone">
                Tone
              </label>
              <select
                id="tone"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
              >
                <option value="Professional">Professional</option>
                <option value="Conversational">Conversational</option>
                <option value="Educational">Educational</option>
                <option value="Inspirational">Inspirational</option>
                <option value="Humorous">Humorous</option>
                <option value="Authoritative">Authoritative</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2" htmlFor="length">
                Approximate Length
              </label>
              <select
                id="length"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                value={length}
                onChange={(e) => setLength(e.target.value)}
              >
                <option value="Short (300-500 words)">Short (300-500 words)</option>
                <option value="Medium (800-1200 words)">Medium (800-1200 words)</option>
                <option value="Long (1500-2000 words)">Long (1500-2000 words)</option>
              </select>
            </div>

            <div>
              <button
                type="submit"
                className="w-full py-3 px-6 border border-transparent rounded-xl shadow-lg text-white font-medium text-base bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200 transform hover:scale-[1.02]"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating your blog post...
                  </div>
                ) : (
                  'Create Blog Post'
                )}
              </button>
            </div>
          </form>
          </div>
        </div>

        {/* Right column - Generated blog post with editor */}
        <div className="w-full lg:w-3/5 relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-6">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 opacity-70"></div>
          
          <div className="relative z-10">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">{blogPost ? 'Your Blog Post' : 'Generated Blog Post'}</h2>
              {blogPost && (
                <div className="flex space-x-2">
                  <button
                    onClick={handleEditToggle}
                    className={`${editMode ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white' : 'bg-white/40 backdrop-blur-sm text-indigo-700 border border-white/40'} text-xs py-1.5 px-3 rounded-lg shadow-sm hover:shadow transition-all duration-200`}
                  >
                    {editMode ? 'Save Changes' : 'Edit Content'}
                  </button>
                  <button
                    onClick={copyToClipboard}
                    className="bg-white/40 backdrop-blur-sm text-indigo-700 border border-white/40 text-xs py-1.5 px-3 rounded-lg shadow-sm hover:shadow transition-all duration-200"
                    title="Copy as plain text"
                  >
                    Copy Text
                  </button>
                  <button
                    onClick={copyHtmlToClipboard}
                    className="bg-white/40 backdrop-blur-sm text-indigo-700 border border-white/40 text-xs py-1.5 px-3 rounded-lg shadow-sm hover:shadow transition-all duration-200"
                    title="Copy with HTML formatting"
                  >
                    Copy HTML
                  </button>
                </div>
              )}
            </div>
          </div>

          {blogPost ? (
            <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl overflow-auto" style={{ maxHeight: '65vh' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-indigo-50/20 opacity-60"></div>
              <div className="relative z-10 p-6">
                <h1 className="text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-700">{blogPost.title}</h1>
                {editMode ? (
                  <div
                    ref={contentEditorRef}
                    contentEditable
                    className="prose max-w-none outline-none focus:ring-2 focus:ring-blue-400 p-4 rounded-xl bg-white/50 backdrop-blur-sm border border-white/40 shadow-sm"
                    dangerouslySetInnerHTML={{ __html: editedContent }}
                    onInput={handleContentChange}
                  />
                ) : (
                  <div 
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: editedContent || blogPost.content }}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg text-center overflow-hidden" style={{ height: '65vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-indigo-50/20 opacity-60"></div>
              {loading ? (
                <div className="relative z-10 flex flex-col items-center justify-center p-6">
                  <div className="w-16 h-16 mb-4">
                    <svg className="animate-spin w-full h-full text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <div className="text-indigo-600 font-medium">Creating your blog post...</div>
                </div>
              ) : (
                <div className="relative z-10 flex flex-col items-center justify-center p-6">
                  <div className="w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 opacity-60">
                    <svg className="w-8 h-8 text-indigo-500 opacity-80" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                  </div>
                  <div className="text-gray-700 font-medium mb-1">Your blog post will appear here</div>
                  <p className="text-gray-500 text-sm max-w-md">Fill out the form and click "Create Blog Post" to generate your content</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPostCreator;
